import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  MessageSquare,
  ClipboardList,
  TrendingUp,
  ShieldCheck,
  LogOut,
  Search,
  Download,
  FileSpreadsheet,
  FileText,
  ChevronLeft,
  ChevronRight,
  Activity,
  Key,
  Loader2,
  RefreshCw,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { adminAxios, getAdmin, clearAdminSession, ADMIN_API } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            {label}
          </p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const admin = getAdmin();
  const [tab, setTab] = useState("overview");

  // Overview
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Users
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logRole, setLogRole] = useState("all");

  // Settings
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await adminAxios.get("/stats");
      setStats(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await adminAxios.get("/users", {
        params: { page, page_size: pageSize, search: search || undefined },
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await adminAxios.get("/login-logs", {
        params: { role: logRole === "all" ? undefined : logRole, limit: 200 },
      });
      setLogs(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "overview") loadStats();
    if (tab === "users") loadUsers();
    if (tab === "logs") loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab === "users") loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (tab === "logs") loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logRole]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams({ format });
      if (search) params.append("search", search);
      const resp = await fetch(`${ADMIN_API}/users/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "xlsx" ? "xlsx" : "csv";
      a.download = `users_${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()} successfully`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    toast.success("Logged out");
    navigate("/admin/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setChangingPassword(true);
    try {
      await adminAxios.post("/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500 leading-none">YourAIMentor</p>
              <h1 className="text-lg font-bold text-slate-800">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-xs text-slate-500">Signed in as</span>
              <span className="text-sm font-medium text-slate-700">
                {admin?.email}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-grid mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="logs">Login Logs</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-800">
                Platform Overview
              </h2>
              <Button variant="outline" size="sm" onClick={loadStats} disabled={statsLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${statsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            {statsLoading && !stats ? (
              <div className="flex items-center justify-center h-60">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  icon={Users}
                  label="Total Users"
                  value={stats?.total_users ?? 0}
                  accent="bg-gradient-to-br from-indigo-500 to-indigo-600"
                />
                <StatCard
                  icon={MessageSquare}
                  label="Total Conversations"
                  value={stats?.total_conversations ?? 0}
                  accent="bg-gradient-to-br from-teal-500 to-teal-600"
                />
                <StatCard
                  icon={Activity}
                  label="Total Messages"
                  value={stats?.total_messages ?? 0}
                  accent="bg-gradient-to-br from-amber-500 to-orange-500"
                />
                <StatCard
                  icon={ClipboardList}
                  label="Total Assessments"
                  value={stats?.total_assessments ?? 0}
                  accent="bg-gradient-to-br from-rose-500 to-pink-600"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Signups (7 days)"
                  value={stats?.signups_last_7_days ?? 0}
                  accent="bg-gradient-to-br from-emerald-500 to-green-600"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Signups (30 days)"
                  value={stats?.signups_last_30_days ?? 0}
                  accent="bg-gradient-to-br from-sky-500 to-blue-600"
                />
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-slate-800">
                Users{" "}
                <span className="text-sm font-normal text-slate-500">
                  ({total} total)
                </span>
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport("csv")}
                  disabled={exporting}
                >
                  <FileText className="w-4 h-4 mr-2" /> CSV
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleExport("xlsx")}
                  disabled={exporting}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                </Button>
              </div>
            </div>

            <form
              onSubmit={handleSearch}
              className="flex gap-2 mb-4 bg-white p-3 rounded-lg border border-slate-200"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" disabled={usersLoading}>
                Search
              </Button>
              {search && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                    setTimeout(loadUsers, 0);
                  }}
                >
                  Clear
                </Button>
              )}
            </form>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Signup Date</TableHead>
                      <TableHead className="font-semibold text-center">Conv.</TableHead>
                      <TableHead className="font-semibold text-center">Assess.</TableHead>
                      <TableHead className="font-semibold">Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usersLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10">
                          <Loader2 className="w-5 h-5 animate-spin inline-block text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u) => (
                        <TableRow key={u.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium text-slate-800">
                            {u.name || "—"}
                          </TableCell>
                          <TableCell className="text-slate-600">{u.email}</TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">
                            {formatDate(u.created_at)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{u.conversations_count}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{u.assessments_count}</Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">
                            {formatDate(u.last_active)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || usersLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || usersLoading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="mt-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-slate-800">Login Logs</h2>
              <div className="flex gap-2">
                <Button
                  variant={logRole === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLogRole("all")}
                >
                  All
                </Button>
                <Button
                  variant={logRole === "admin" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLogRole("admin")}
                >
                  Admin
                </Button>
                <Button
                  variant={logRole === "user" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLogRole("user")}
                >
                  Users
                </Button>
                <Button variant="outline" size="sm" onClick={loadLogs}>
                  <RefreshCw className={`w-4 h-4 ${logsLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold">When</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">IP Address</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10">
                          <Loader2 className="w-5 h-5 animate-spin inline-block text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10 text-slate-500">
                          No login logs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id} className="hover:bg-slate-50">
                          <TableCell className="text-slate-600 whitespace-nowrap">
                            {formatDate(log.timestamp)}
                          </TableCell>
                          <TableCell className="text-slate-700">{log.email}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                log.role === "admin"
                                  ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100"
                                  : "bg-slate-100 text-slate-700 hover:bg-slate-100"
                              }
                            >
                              {log.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 font-mono text-xs">
                            <span className="inline-flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {log.ip}
                            </span>
                          </TableCell>
                          <TableCell>
                            {log.success ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                Success
                              </Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                                Failed
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-2">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Settings</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Change Admin Password</h3>
                    <p className="text-xs text-slate-500">
                      Reset the admin password used to sign in
                    </p>
                  </div>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Current password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password">New password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm new password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...
                      </>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Admin Account</h3>
                    <p className="text-xs text-slate-500">Current session details</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-500">Email</span>
                    <span className="font-medium text-slate-800">{admin?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-500">Name</span>
                    <span className="font-medium text-slate-800">{admin?.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-500">Admin since</span>
                    <span className="font-medium text-slate-800">
                      {formatDate(admin?.created_at)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-500">Role</span>
                    <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                      Administrator
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
